import React, { useEffect, useState } from "react";
import { Coins, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";

/**
 * TopUpOverlayDialog — in-place top-up modal for Producer-tier users
 * (and anyone else we want to spare the trip to /pricing). Renders the
 * same credit packages as the pricing page's top-up box; on selection
 * POSTs to /payments/topup/checkout and redirects to Stripe.
 *
 * Why a separate component, not just reuse the pricing-page card?
 * Because the trigger lives in the header, which means it can fire
 * from ANY route. The user keeps their current page state — they
 * see the modal, pick a package, Stripe takes over, and after
 * checkout they land back on the dashboard with the updated
 * balance. No "find your way back from the pricing page" friction.
 */
const TopUpOverlayDialog = ({ open, onOpenChange }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null); // package_id being purchased
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!open || fetched) return;
    api.get(`/payments/packages`)
      .then(r => { setPackages(r.data.topup || []); setFetched(true); })
      .catch(() => { /* silent; user will see empty list and can retry next open */ });
  }, [open, fetched]);

  const buy = async (packageId) => {
    setLoading(packageId);
    try {
      const res = await api.post(`/payments/topup/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      // Stripe takes over — full-page redirect is the only thing they
      // accept for hosted Checkout. After payment lands the user is
      // returned to /dashboard?session_id=… via the success_url config
      // and usePaymentRedirectHandler claims the credits.
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md" data-testid="topup-overlay">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-emerald-400" />
            Top-up Your Credits
          </DialogTitle>
          <DialogDescription className="text-gray-400 inline-flex items-center gap-1.5 text-xs">
            <ShieldCheck className="h-3 w-3 text-emerald-400/70" />
            Stripe-secured — you&apos;ll return here after checkout.
          </DialogDescription>
        </DialogHeader>

        {!fetched && (
          <div className="flex justify-center py-6" data-testid="topup-overlay-loading">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        )}

        {fetched && packages.length === 0 && (
          <p className="text-center text-gray-500 text-sm py-4">
            No top-up packages available right now. Try again in a moment.
          </p>
        )}

        {fetched && packages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {packages.map(pkg => (
              <button
                key={pkg.id}
                onClick={() => buy(pkg.id)}
                disabled={!!loading}
                data-testid={`topup-overlay-pkg-${pkg.id}`}
                className="bg-gray-800 hover:bg-emerald-600/20 border border-emerald-500/30 hover:border-emerald-400 rounded-lg p-3 text-center transition-all disabled:opacity-50"
              >
                <div className="text-emerald-300 font-bold text-lg">{pkg.credits} cr</div>
                <div className="text-gray-400 text-xs">${pkg.amount_usd}</div>
                {loading === pkg.id && (
                  <div className="text-[10px] text-emerald-400 mt-1">Opening…</div>
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TopUpOverlayDialog;
